import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  addBusinessAppComponent,
  businessAppCandidatesQuery,
  businessAppQuery,
  removeBusinessApp,
  removeBusinessAppComponent,
  type BusinessAppComponent,
  type BusinessAppDetail,
  type Candidate,
  type SupportContact,
} from '../api/businessApps'
import { ApiError } from '../api/client'
import { moduleOf, shellQuery } from '../api/shell'
import { AppsAttentionPill } from '../ui/AppsAttentionMark'
import { Forbidden } from '../ui/Forbidden'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { AppAvailability } from './AppAvailability'
import { AppIncidents } from './AppIncidents'
import { AppComponentDatabases } from './AppComponentDatabases'
import { AppMaintenance, MaintenanceBadge } from './AppMaintenance'
import { AppVendorCases } from './AppVendorCases'
import { ExpiryBanner, ExpiryCard } from './expiry/AppExpiry'
import { AppRunbook } from './AppRunbook'
import { BackupCard, BackupDetailBand } from './backup/Backup'
import { VendorPerformanceCard } from './vendors/VendorPerformanceCard'
import { AppDependencies } from './dependencies/AppDependencies'
import { CompletenessCard, LifecycleBadges, NextReviewNote, SECTION_ANCHOR } from './Lifecycle'
import { AppFormModal, CriticalityPill, failed, HealthPill, STYLES, StatePill } from './parts'
import { AppVersionsCard } from './versions/AppVersionsCard'
import { SupportModal } from './support/SupportModal'

/**
 * Fisa unei aplicatii de business: detaliile, componentele (aparate si CI-uri,
 * fiecare cu rolul ei), suportul furnizorului cu drumul de escaladare, si ce e
 * deschis acum pe componente.
 *
 * Componentele cu CI sunt muchii `depends_on` in CMDB (aplicatie -> server),
 * deci analiza de impact a CMDB-ului vede aplicatia cand cade serverul. Un
 * aparat fara CI ramane componenta, dar fara muchie - randul o spune.
 */
export function ApplicationDetailPage({ id }: { id: number }) {
  const shell = useQuery(shellQuery(moduleOf('/applications')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const page = useQuery(businessAppQuery(id))

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error instanceof ApiError && page.error.status === 404) {
    return (
      <Content>
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <i className="fas fa-exclamation-circle"></i> No such application, or it belongs to another customer.
        </div>
      </Content>
    )
  }
  if (page.error) {
    return (
      <Content>
        <div className="alert alert-danger" role="alert" style={{ margin: 20 }}>
          Something went wrong: {page.error.message}
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />
  const data = page.data as BusinessAppDetail & { error?: string; message?: string }
  if (data.error === 'not_ready') {
    return (
      <Content>
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <i className="fas fa-database"></i> {data.message}
        </div>
      </Content>
    )
  }

  return (
    <Content>
      <AppsAttentionPill count={shell.data?.appsAttention} />
      <Detail page={data} />
    </Content>
  )
}

function Detail({ page }: { page: BusinessAppDetail }) {
  const app = page.app
  const query = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [editingSupport, setEditingSupport] = useState(false)
  const reload = () => void query.invalidateQueries({ queryKey: ['business-apps'] })

  const remove = () => {
    if (!window.confirm(`Remove ${app.name}? Its components and support contacts go with it.`)) return
    removeBusinessApp(app.id)
      .then((result) => {
        toast(result.message, 'Applications', result.status === 'OK' ? 'success' : 'error')
        if (result.status === 'OK') {
          reload()
          window.location.assign('/app/applications')
        }
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
  }

  return (
    <>
      <div className="adm-record-head">
        <h3>
          <a href="/app/applications" className="adm-muted" style={{ fontWeight: 'normal' }}>
            Applications
          </a>{' '}
          / {app.name} <LifecycleBadges app={app} /> <MaintenanceBadge appId={app.id} /> <NextReviewNote app={app} />
        </h3>
        <span className="adm-record-sub">
          {app.companyName}
          {app.vendor !== '' && <> &middot; {app.vendor}</>}
          {app.version !== '' && <> {app.version}</>}
        </span>
      </div>

      <div className="adm-bar">
        <div className="adm-field">
          <label>Criticality</label>
          <div>
            <CriticalityPill value={app.criticality} label={app.criticalityLabel} />
          </div>
        </div>
        <div className="adm-field">
          <label>State</label>
          <div>
            <StatePill state={app.state} label={app.stateLabel} />
          </div>
        </div>
        <div className="adm-field">
          <label>Open tickets</label>
          <div>
            {app.openTickets}
            {app.openAlarms > 0 && <span className="adm-muted"> ({app.openAlarms} alarms)</span>}
          </div>
        </div>
        <div className="adm-field">
          <label>CMDB</label>
          <div>
            {page.cmdb.ciId !== null ? (
              <a href={`/app/cmdb/view/${page.cmdb.ciId}`} target="_blank" rel="noopener noreferrer">configuration item #{page.cmdb.ciId}</a>
            ) : (
              <span className="adm-muted">not in the CMDB</span>
            )}
          </div>
        </div>
        <div className="ci-actions">
          {page.canEdit && (
            <button id="appEdit" className="btn btn-primary btn-sm" onClick={() => setEditing(true)}>
              <i className="fa fa-pencil-alt"></i> Edit
            </button>
          )}
          {page.canDelete && (
            <button id="appRemove" className="btn btn-danger btn-sm" onClick={remove}>
              <i className="fa fa-trash"></i> Remove
            </button>
          )}
        </div>
      </div>

      <ExpiryBanner appId={app.id} />
      <CompletenessCard page={page} />
      <BackupDetailBand appId={app.id} />

      <div className="row">
        <div className="col-md-6">
          <DetailsCard page={page} />
          <SupportCard page={page} onEdit={() => setEditingSupport(true)} />
          <ExpiryCard appId={app.id} />
          <AppRunbook appId={app.id} />
          <AppVersionsCard appId={app.id} />
        </div>
        <div className="col-md-6">
          <ComponentsCard page={page} onChanged={reload} />
          <ImpactCard page={page} />
          <AppIncidents appId={app.id} />
          <AppAvailability appId={app.id} />
          <BackupCard appId={app.id} />
          <AppDependencies appId={app.id} />
          <AppVendorCases appId={app.id} />
          <VendorPerformanceCard appId={app.id} />
          <AppMaintenance appId={app.id} />
        </div>
      </div>

      {editing && (
        <AppFormModal
          app={app}
          companyId={app.companyId}
          criticality={page.criticality}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
      {editingSupport && (
        <SupportModal
          appId={app.id}
          appName={app.name}
          onClose={() => setEditingSupport(false)}
          onSaved={() => {
            setEditingSupport(false)
            reload()
          }}
        />
      )}
    </>
  )
}

function Section({ title, note, id }: { title: string; note?: string; id?: string }) {
  return (
    <div className="adm-section" id={id}>
      <h4>{title}</h4>
      {note !== undefined && <span className="adm-section-note">{note}</span>}
    </div>
  )
}

function DetailsCard({ page }: { page: BusinessAppDetail }) {
  const app = page.app
  const row = (label: string, value: string, pre = false) => (
    <tr>
      <th style={{ width: 140 }}>{label}</th>
      <td style={pre ? { whiteSpace: 'pre-wrap' } : undefined}>{value !== '' ? value : <span className="adm-muted">-</span>}</td>
    </tr>
  )

  return (
    <>
      <Section title="Details" id={SECTION_ANCHOR.details} />
      <div className="adm-card">
        <table className="table adm-list" id="appDetails">
          <tbody>
            {row('Vendor', app.vendor)}
            {row('Version', app.version)}
            {row('Customer', app.companyName)}
            {row('Owner', app.ownerName)}
            {row('Description', app.description, true)}
            {row('Notes', app.notes, true)}
          </tbody>
        </table>
      </div>
    </>
  )
}

function levelLabel(level: number): string {
  return level > 0 ? `Level ${level}` : 'Contact'
}

function sortedContacts(contacts: SupportContact[]): SupportContact[] {
  return [...contacts].sort((a, b) => (a.level === 0 ? 99 : a.level) - (b.level === 0 ? 99 : b.level))
}

function SupportCard({ page, onEdit }: { page: BusinessAppDetail; onEdit(): void }) {
  const s = page.support
  const empty = s.vendor === '' && page.contacts.length === 0 && s.contractNo === ''

  return (
    <>
      <div className="adm-section" id={SECTION_ANCHOR.support}>
        <h4>Support &amp; escalation</h4>
        <span className="adm-section-note">who to call at the vendor, for this customer</span>
        {page.canEdit && (
          <button type="button" id="supportEdit" className="btn btn-default btn-xs" style={{ marginLeft: 'auto' }} onClick={onEdit}>
            <i className="fa fa-pencil-alt"></i> Edit support
          </button>
        )}
      </div>
      <div className="adm-card" id="appSupport">
        {empty ? (
          <p className="text-muted" style={{ padding: '10px 15px' }}>
            No support details yet. Fill in the vendor or partner, the contract and the people to call - the
            ticket's "Escalate" button uses them.
          </p>
        ) : (
          <>
            <table className="table adm-list">
              <tbody>
                <tr>
                  <th style={{ width: 140 }}>Vendor / partner</th>
                  <td>{s.vendor !== '' ? s.vendor : <span className="adm-muted">-</span>}</td>
                </tr>
                <tr>
                  <th>Ticket portal</th>
                  <td>
                    {s.portalUrl !== '' ? (
                      <a href={s.portalUrl} target="_blank" rel="noopener noreferrer">
                        {s.portalUrl}
                      </a>
                    ) : (
                      <span className="adm-muted">-</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Contract</th>
                  <td>{s.contractNo !== '' ? s.contractNo : <span className="adm-muted">-</span>}</td>
                </tr>
                <tr>
                  <th>Support hours</th>
                  <td>{s.scheduleLabel !== '' ? s.scheduleLabel : <span className="adm-muted">not set (response counted in calendar hours)</span>}</td>
                </tr>
                <tr>
                  <th>Response time</th>
                  <td>{s.responseHours !== null ? `${s.responseHours} h` : <span className="adm-muted">-</span>}</td>
                </tr>
                <tr>
                  <th>They need on a case</th>
                  <td style={{ whiteSpace: 'pre-wrap' }}>{s.caseNeeds !== '' ? s.caseNeeds : <span className="adm-muted">-</span>}</td>
                </tr>
              </tbody>
            </table>
            {page.contacts.length > 0 && (
              <table className="table adm-list" id="appContacts">
                <thead>
                  <tr>
                    <th>Level</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedContacts(page.contacts).map((c) => (
                    <tr key={c.id}>
                      <td className="adm-nowrap">
                        {levelLabel(c.level)}
                        {c.levelNote !== '' && <div className="adm-muted small">{c.levelNote}</div>}
                      </td>
                      <td>
                        {c.name}
                        {c.role !== '' && <div className="adm-muted small">{c.role}</div>}
                      </td>
                      <td className="adm-nowrap">{c.phone !== '' ? <a href={`tel:${c.phone.replace(/[^+\d]/g, '')}`}>{c.phone}</a> : ''}</td>
                      <td>{c.email !== '' ? <a href={`mailto:${c.email}`}>{c.email}</a> : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </>
  )
}

function ComponentsCard({ page, onChanged }: { page: BusinessAppDetail; onChanged(): void }) {
  const [adding, setAdding] = useState(false)

  const remove = (c: BusinessAppComponent) => {
    if (!window.confirm(`Remove ${c.name} (${c.roleLabel}) from ${page.app.name}?`)) return
    removeBusinessAppComponent(c.id)
      .then((result) => {
        toast(result.message, 'Applications', result.status === 'OK' ? 'success' : 'error')
        onChanged()
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
  }

  return (
    <>
      <div className="adm-section" id={SECTION_ANCHOR.components}>
        <h4>Components</h4>
        <span className="adm-section-note">the devices and items the application runs on</span>
        {page.canEdit && (
          <button type="button" id="componentAdd" className="btn btn-success btn-xs" style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)}>
            <i className="fas fa-plus"></i> Add component
          </button>
        )}
      </div>
      {!page.cmdb.propagates && page.components.some((c) => c.linked) && (
        <div className="alert alert-info small" style={{ margin: '0 0 10px' }}>
          The links are in the CMDB, but the impact analysis does not follow <code>depends_on</code> for this provider,
          so it will not name this application when a server goes down.
        </div>
      )}
      <div className="adm-card">
        {page.components.length === 0 ? (
          <p className="text-muted" style={{ padding: '10px 15px' }}>
            No components yet. Add the database server, the application server and the rest.
          </p>
        ) : (
          <table className="table adm-list" id="appComponents">
            <thead>
              <tr>
                <th>Component</th>
                <th>Role</th>
                <th>Health</th>
                <th>CMDB</th>
                {page.canEdit && <th></th>}
              </tr>
            </thead>
            <tbody>
              {page.components.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.deviceId !== null ? (
                      <a href={`/app/inventoree/device/${c.deviceId}`} target="_blank" rel="noopener noreferrer">{c.name}</a>
                    ) : c.ciId !== null ? (
                      <a href={`/app/cmdb/view/${c.ciId}`} target="_blank" rel="noopener noreferrer">{c.name}</a>
                    ) : (
                      c.name
                    )}
                    {c.note !== '' && <div className="adm-muted small">{c.note}</div>}
                    {c.role === 1 && c.deviceId !== null && <AppComponentDatabases appId={page.app.id} deviceId={c.deviceId} />}
                  </td>
                  <td className="adm-nowrap">{c.roleLabel}</td>
                  <td>
                    <HealthPill health={c.health} label={c.healthLabel} />
                  </td>
                  <td className="adm-nowrap">
                    {c.linked ? (
                      <span className="adm-pill adm-pill-ok" title="depends_on link in the CMDB">In CMDB</span>
                    ) : (
                      <span className="adm-pill" title="Not promoted to the CMDB: the impact analysis does not see it">
                        Not in CMDB
                      </span>
                    )}
                  </td>
                  {page.canEdit && (
                    <td className="text-right">
                      <button type="button" className="btn btn-default btn-xs" title="Remove" aria-label={`Remove ${c.name}`} onClick={() => remove(c)}>
                        <i className="fa fa-times"></i>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {adding && (
        <AddComponentModal
          page={page}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false)
            onChanged()
          }}
        />
      )}
    </>
  )
}

function AddComponentModal({ page, onClose, onAdded }: { page: BusinessAppDetail; onClose(): void; onAdded(): void }) {
  const [draft, setDraft] = useState('')
  const [search, setSearch] = useState('')
  const [picked, setPicked] = useState<Candidate | null>(null)
  const [role, setRole] = useState<number>(page.roles[0]?.value ?? 9)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const hits = useQuery(businessAppCandidatesQuery(page.app.id, search))

  useEffect(() => {
    if (draft === search) return
    const t = window.setTimeout(() => setSearch(draft), 300)
    return () => window.clearTimeout(t)
  }, [draft, search])

  const submit = () => {
    if (picked === null) {
      toast('Choose a device or a configuration item.', 'Applications', 'error')
      return
    }
    setBusy(true)
    addBusinessAppComponent(page.app.id, { kind: picked.kind, ref_id: picked.id, role, note })
      .then((result) => {
        if (result.status !== 'OK') {
          toast(result.message, 'Applications', 'error')
          return
        }
        toast(result.message, 'Applications')
        onAdded()
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id="componentModal"
      open
      onClose={onClose}
      title={`Add a component to ${page.app.name}`}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" id="componentSave" disabled={busy || picked === null} onClick={submit}>
            Add
          </button>
        </>
      }
    >
      <div className="form-group">
        <label htmlFor="componentSearch">Device or CMDB item of {page.app.companyName}</label>
        <input id="componentSearch" className="form-control" placeholder="name, e.g. BUCPS-SQL" value={draft} onChange={(e) => setDraft(e.target.value)} />
      </div>
      <div className="list-group" style={{ maxHeight: 220, overflowY: 'auto' }} id="componentHits">
        {(hits.data?.hits ?? []).map((h) => (
          <button
            type="button"
            key={`${h.kind}-${h.id}`}
            className={`list-group-item${picked?.kind === h.kind && picked.id === h.id ? ' active' : ''}`}
            onClick={() => setPicked(h)}
          >
            {h.name} <span className="small" style={{ opacity: 0.7 }}>{h.detail}</span>
          </button>
        ))}
        {hits.data && hits.data.hits.length === 0 && <p className="text-muted">Nothing matches.</p>}
      </div>
      <div className="row">
        <div className="col-sm-5 form-group">
          <label htmlFor="componentRole">Role</label>
          <select id="componentRole" className="form-control" value={role} onChange={(e) => setRole(Number(e.target.value))}>
            {page.roles.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-7 form-group">
          <label htmlFor="componentNote">Note</label>
          <input id="componentNote" className="form-control" maxLength={255} placeholder="e.g. database CHARISMA_PROD" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>
    </Modal>
  )
}

function ImpactCard({ page }: { page: BusinessAppDetail }) {
  return (
    <>
      <Section title="Impact now" note="open tickets and alarms on the components" />
      <div className="adm-card" id="appImpact">
        {page.impact.length === 0 ? (
          <p className="text-muted" style={{ padding: '10px 15px' }}>
            Nothing open on the components.
          </p>
        ) : (
          <table className="table adm-list">
            <tbody>
              {page.impact.map((t) => (
                <tr key={t.ticketId}>
                  <td className="adm-nowrap adm-muted">{t.created ?? ''}</td>
                  <td>
                    <a href={`/app/easeedesk/ticket/${t.ticketId}`} target="_blank" rel="noopener noreferrer">
                      #{t.ticketId} {t.subject}
                    </a>
                    {t.component !== '' && <div className="adm-muted small">on {t.component}</div>}
                  </td>
                  <td className="text-right">
                    <span className={`adm-pill ${t.alarm ? 'adm-pill-bad' : 'adm-pill-warn'}`}>{t.alarm ? 'Alarm' : 'Ticket'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}
